import { getEffectiveHassConfig } from '../hass/hassEffective.js';
import { getTemplateObjectRecords } from './designerObjectIds.js';
import { validateTemplateTree } from './templateTree.js';
import { serializeObjectActions } from '../utils/objectActions.js';

/**
 * Build the Designer-side, page-independent definition of a template.
 *
 * This is intentionally not wired into device export yet. It is a neutral
 * representation for validation/tests and future runtime serialization. Page,
 * source, tag, and current state are excluded from the visual definition
 * because those belong to an invocation or runtime cache. Semantic actions and
 * binding metadata are returned separately so they remain serializable without
 * turning into embedded JSONL or concrete preview state.
 */
export function serializeTemplateDefinition(templatePage, { templateVisibilityMode = 'semantic' } = {}) {
    const tree = validateTemplateTree(templatePage);
    if (!tree.valid) return { template: Number(templatePage?.haspid), objects: [], metadata: [], errors: tree.errors };

    const errors = [];
    const objects = getTemplateObjectRecords(templatePage, '', { includePage: false }).map(record => {
        const definition = { ...record };
        delete definition.page;
        delete definition.hass_config;
        delete definition.tag;
        delete definition.targetPage;
        delete definition.underlayPage;
        delete definition.sourceObject;
        delete definition.previewPage;
        delete definition.uuid;
        delete definition.automation;
        delete definition.call_service;
        delete definition.entityId;
        delete definition.bindings;
        delete definition.events;
        delete definition.properties;
        definition.hidden ??= false;

        if (Array.isArray(record.actions) && record.actions.length > 0) {
            const serialized = serializeObjectActions(record.actions, {
                sourceNode: record,
                templateVisibilityMode,
                resolveTemplateTarget: id => Number(id) === Number(templatePage?.haspid)
                    ? templatePage : null,
            });
            errors.push(...serialized.errors.map(error =>
                `Template T${templatePage?.haspid} object ${record.id}: ${error.message}`));
            if (Object.keys(serialized.action).length > 0) definition.action = serialized.action;
        }
        delete definition.actions;
        return definition;
    });

    const metadata = [];
    const visit = node => {
        if (!node || node.obj === 'page' || node.obj === 'template_page') {
            (node?.getChildren?.() ?? []).forEach(visit);
            return;
        }
        if (node.haspid !== undefined && node.haspid !== null) {
            // Includes the template page's Home Assistant flow graph.
            const hassConfig = getEffectiveHassConfig(node) ?? {};
            const entry = { id: Number(node.haspid) };
            if (Array.isArray(hassConfig.automation) && hassConfig.automation.length > 0) {
                entry.automation = JSON.parse(JSON.stringify(hassConfig.automation));
                for (const model of entry.automation) {
                    for (const binding of model.bindings ?? []) {
                        if (binding.sourceType === 'template') binding.entityId = '';
                    }
                }
            }
            if (entry.automation) metadata.push(entry);
        }
        (node.getChildren?.() ?? []).forEach(visit);
    };
    visit(templatePage);

    return {
        template: Number(templatePage?.haspid),
        objects,
        metadata,
        errors,
    };
}

/**
 * Flatten one definition into the records that belong in standard pages.jsonl.
 * Runtime metadata is intentionally not part of this wire representation;
 * Designer/HA configuration remains available from the Designer model and the
 * non-device definition object above.
 */
export function serializeTemplateDefinitionRecords(templatePage, options = {}) {
    const definition = serializeTemplateDefinition(templatePage, options);
    if (definition.errors.length > 0) return definition;
    const header = { template: definition.template };
    return {
        ...definition,
        records: [header, ...definition.objects],
    };
}
